import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
//import { useContentIsActive } from '../../components/FeatureCard/ContentIsActiveContext';
import { Button, Menu, MenuItem, Popover, PopoverInteractionKind, Position, Checkbox } from '@blueprintjs/core';

import { emit, emitJson } from '../websocket/websocketSlice'
import { selectThemeList, selectCurrentTheme, selectTheme } from './ThemeSlice'
import { selectSkipIntro, toggleIntro } from '../behaviourControls/behaviourControlsSlice';
import './ThemeSelectionPanel.css';

/**
 * This component communicates with the Redux store to manage the selected theme 
 * and the list of available themes. It also provides the ability to hide or show themes 
 * and stores this information in local storage, so that hidden themes persist across sessions.
 * 
 * Features:
 * - Displays a list of available themes in a dropdown menu.
 * - Allows users to select a theme by clicking on it.
 * - Provides a checkbox to hide or show individual themes.
 * - Hidden themes are stored in the local storage and persist across sessions.
 * - Option to toggle visibility of hidden themes within the menu.
 * 
 * The component also interacts with the backend (Unity) to request the list of themes 
 * when the component mounts.
 * 
 * @component
 * @returns {JSX.Element} A rendered `ThemeSelectionPanel` component with theme selection options.
 */
const ThemeSelectionPanel = () => {
    const dispatch = useDispatch();
    const themes = useSelector(selectThemeList);
    const selectedTheme = useSelector(selectCurrentTheme);
    const intro = useSelector(selectSkipIntro);

    const [hiddenThemes, setHiddenThemes] = useState(() => {
        const saved = localStorage.getItem('hiddenThemes');
        return saved ? JSON.parse(saved) : [];
    });

    const [hiddenSectionExpanded, setHiddenSectionExpanded] = useState(false);

    // Request themes from Unity when the component mounts
    useEffect(() => {
        requestThemes();
    }, []);

    // Save hidden themes to local storage
    useEffect(() => {
        localStorage.setItem('hiddenThemes', JSON.stringify(hiddenThemes));
    }, [hiddenThemes]);

    // Function to handle theme selection
    const handleThemeChange = (themeChange) => {
        dispatch(selectTheme(themeChange));
        emit({
            type: 'themeChange',
            theme: themeChange,
            skip: true, // ToDo
        });
    };

    // Function to toggle a theme's visibility
    const toggleThemeVisibility = (theme) => {
        setHiddenThemes((prev) => {
            if (prev.includes(theme)) {
                return prev.filter((t) => t !== theme);
            } else {
                return [...prev, theme];
            }
        });
    };

    const visibleThemes = themes.filter((theme) => !hiddenThemes.includes(theme));
    const hiddenThemesList = themes.filter((theme) => hiddenThemes.includes(theme));

    return (
        <div className="theme-selection-panel">
            <h6>Select the subject of the lesson</h6>
            {themes.length > 0 ? (
                <Popover
                    interactionKind={PopoverInteractionKind.CLICK}
                    position={Position.BOTTOM_LEFT}
                    modifiers={{
                        preventOverflow: { enabled: true },
                        hide: { enabled: false },
                    }}
                    content={
                        <div style={{ maxHeight: '50vh', overflowY: 'auto', padding: '0.5rem' }}
                            onClick={(e) => e.stopPropagation()}>
                            <Menu>
                                {visibleThemes.map((theme) => (
                                    <MenuItem
                                        key={theme}
                                        text={
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <Checkbox
                                                    checked={!hiddenThemes.includes(theme)}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        toggleThemeVisibility(theme);
                                                    }}
                                                    style={{ marginRight: '0.5rem' }}
                                                />
                                                {theme}
                                            </div>
                                        }
                                        onClick={(e) => {
                                            if (e.target.type !== 'checkbox') {
                                                handleThemeChange(theme);
                                            } else {
                                                e.preventDefault();
                                            }
                                        }}
                                        active={theme === selectedTheme}
                                    />
                                ))}
                            </Menu>
                            {hiddenThemesList.length > 0 && (
                                <div className="hidden-themes-section">
                                    <Button
                                        minimal
                                        text={hiddenSectionExpanded ? 'Hide Hidden Themes' : 'Show Hidden Themes'}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setHiddenSectionExpanded(!hiddenSectionExpanded);
                                        }}
                                    />
                                    {hiddenSectionExpanded && (
                                        <Menu style={{ marginTop: '0.5rem' }}>
                                            {hiddenThemesList.map((theme) => (
                                                <MenuItem
                                                    key={theme}
                                                    text={
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <Checkbox
                                                                checked={!hiddenThemes.includes(theme)}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleThemeVisibility(theme);
                                                                }}
                                                                style={{ marginRight: '0.5rem' }}
                                                            />
                                                            {theme}
                                                        </div>
                                                    }
                                                    onClick={(e) => {
                                                        if (e.target.type !== 'checkbox') {
                                                            handleThemeChange(theme);
                                                        } else {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    active={theme === selectedTheme}
                                                />
                                            ))}
                                        </Menu>
                                    )}
                                </div>
                            )}
                        </div>
                    }
                >
                    <Button text={selectedTheme || 'Select a Theme'} rightIcon="caret-down" />
                </Popover>
            ) : (
                <p>Loading themes from Unity...</p>
            )}
            <div className="skip-intro-section">
                <Checkbox
                    checked={intro}
                    onChange={() => dispatch(toggleIntro())}
                    label="Skip Intro on theme change"
                />
            </div>
        </div>
    );
};


// Request themes from Unity
export const requestThemes = () => {
    console.log('Requesting themes from Unity...');
    emitJson('requestThemes', {});
};

export default ThemeSelectionPanel;